import axios from 'axios';

// Get the base URL from localStorage (environment preference) - called on each request
const getBaseURL = () => {
  const selectedEnvironment = localStorage.getItem('selectedEnvironment') || 'production';
  if (selectedEnvironment === 'development') {
    return '/api-dev';
  }
  return '/api';
};

// Create an axios instance with default configuration
const api = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - dynamically set baseURL on each request based on environment
api.interceptors.request.use(
  config => {
    // Dynamically set baseURL based on current environment
    const baseURL = getBaseURL();
    config.baseURL = baseURL;
    
    // If sending FormData, remove Content-Type header to let axios set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    console.log('[API] Environment:', localStorage.getItem('selectedEnvironment'), '| BaseURL:', baseURL, '| Full URL:', baseURL + config.url);
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/** Pull a human-readable error string from PTAdmin or game-server proxy responses. */
export function extractApiError(error, fallback = 'Request failed') {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim();
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  const statusMsg = data?.status?.error_message;
  if (typeof statusMsg === 'string' && statusMsg.trim()) return statusMsg.trim();
  if (statusMsg != null && String(statusMsg).trim()) return String(statusMsg).trim();
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  return fallback;
}

// Response interceptor - handle common errors
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // 401 on protected APIs is handled by ProtectedRoute via session probe — avoid hard reload to /login
    if (error.response && error.response.status === 401) {
      const url = error.config?.url || '';
      const isPasswordValidation = url.includes('/private-key') || url.includes('/confirm-password');
      const isSessionProbe = url.includes('/auth/session');
      if (!isPasswordValidation && !isSessionProbe) {
        console.warn('API 401 (session may have expired):', url);
      }
    }

    if (error.response?.status === 429) {
      const retryAfter = error.response.headers?.['retry-after'];
      const baseMsg = extractApiError(error, 'Too many requests — rate limit exceeded');
      error.message = retryAfter ? `${baseMsg} (retry after ${retryAfter}s)` : baseMsg;
    }
    
    return Promise.reject(error);
  }
);

export default api;
